/** @jsxImportSource @emotion/react */

import { FC, useEffect, useMemo, useState } from "react";
import { css } from "@emotion/core";
import Button from "ui-modules/components/Form/Button";
import { calculateCurrency } from "../../utils/commons";
import Banks from "../Banks";
import TextField from "ui-modules/components/Form/TextField";
import Skeleton from "ui-modules/components/Misc/Skeleton";
import useTheme from "ui-modules/commons/hooks/use-theme";
import useGlobal from "ui-modules/commons/hooks/use-global";
import useNotification from "ui-modules/commons/hooks/use-notification";

type PaymentPanelTypes = {
  amount: number;
  productId?: string;
  timeInterval?: string;
  requiredInterval?: boolean;
  disabled?: boolean;
  voucherCode?: string;
  editableAmount?: boolean;
  proformasId?: string;
  customerId?: string;
  invoices?: string[];
  loading?: boolean;
  async?: boolean;
  hidePaymentButton?: boolean;
  testPasargad?: "PASARGAD_NEW" | "POST_BANK";
};

const PaymentPanel: FC<PaymentPanelTypes> = (props) => {
  const theme = useTheme();
  const notification = useNotification();
  const customerId = useGlobal()?.global.me?.id || props.customerId || "";

  const {
    amount = 0,
    productId = "0",
    timeInterval = "",
    requiredInterval = false,
    disabled = false,
    voucherCode = "",
    editableAmount = false,
    proformasId,
    invoices = [],
    loading = false,
    async = false,
    hidePaymentButton = false,
    testPasargad,
  } = props;

  const verifyType = async ? "ASYNC" : "SYNC";

  const [bank, setBank] = useState("PASARGAD");

  const [payAmount, setPayAmount] = useState<string | number>(amount);

  useEffect(() => {
    setPayAmount(amount);
  }, [amount]);

  const validForm = () => {
    let valid = false;
    if (bank && bank.length && payAmount && Math.abs(Number(payAmount)) > 0) {
      valid = true;
    }
    if (requiredInterval) {
      if (!timeInterval.length) {
        valid = false;
      }
    }
    if (disabled) {
      valid = false;
    }
    return valid;
  };

  const renderRequestExtraData = () => {
    let extraData: any = {};
    if (timeInterval.length) {
      extraData = { ...extraData, timeInterval: timeInterval };
      if (voucherCode.length) {
        extraData = { ...extraData, voucherCode: voucherCode };
      }
    }
    if (proformasId) {
      extraData = { ...extraData, proformasId };
    }
    if (invoices.length) {
      extraData = { ...extraData, invoices };
    }
    if (Object.keys(extraData).length) {
      extraData = JSON.stringify(extraData);
      if (async) extraData = extraData.replaceAll('"', '\\"'); // For sake of the new async api

      return <input type="hidden" value={extraData} name="requestExtraData" />;
    }
    return null;
  };

  const validateForm = (e: any) => {
    if (!customerId.length) {
      e.preventDefault();
      notification.error({ message: "خطا، لطفا مجددا تلاش نمایید." });
    }
  };

  const handleChangeAmount = (value: number | string) => setPayAmount(value);

  const renderAmount = () => {
    if (editableAmount) {
      return (
        <div style={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center" }}>
          <div style={{ paddingRight: 30 }}>
            <TextField
              label="مبلغ  قابل پرداخت"
              defaultValue={payAmount}
              disabled={disabled}
              onChange={handleChangeAmount}
            />
          </div>
          <span style={{ marginRight: 5 }}>ریال</span>
        </div>
      );
    } else {
      return (
        <span>
          <strong>مبلغ قابل پرداخت:</strong> {calculateCurrency(Math.abs(Number(payAmount)))} ریال
        </span>
      );
    }
  };

  const styles = css`
    .pay-methods {
      display: flex;
      min-height: 300px;
      text-align: center;
      align-items: center;
      justify-content: center;
    }

    .price-box {
      margin: 30px 0 5px 0;
      font-weight: bold;
      color: ${theme.material.textPrimaryOnBackground};
    }

    .loading {
      margin: 30px auto 5px;
    }

    .pay-button {
      width: 200px !important;
      margin-top: 10px !important;
    }

    form {
      margin-bottom: 15px;
    }
  `;

  const verifyUrlPostfix = useMemo(() => {
    if (productId !== "0") {
      return "renew";
    } else {
      return "receipt";
    }
  }, [productId]);

  return (
    <div css={styles}>
      <div className="pay-methods">
        <div>
          {!hidePaymentButton && (
            <>
              <Banks onSelect={setBank} loading={loading} testPasargad={testPasargad} />
              {loading ? (
                <Skeleton width={250} height={20} className="loading" />
              ) : (
                <div className="price-box">{renderAmount()}</div>
              )}
            </>
          )}
          <div>
            <form action="/payment/request" method="POST" target="_self" onSubmit={validateForm}>
              <input type="hidden" name="bank" value={bank} />
              <input type="hidden" name="payerId" value={customerId} />
              <input type="hidden" name="productId" value={productId} />
              <input type="hidden" name="amount" value={Math.abs(Number(payAmount))} />
              <input
                type="hidden"
                name="verifyUrl"
                value={`http://bss-service/api/gateway-payment/${verifyUrlPostfix}`}
              />
              <input type="hidden" name="verifyType" value={verifyType} />
              {renderRequestExtraData()}
              {loading ? (
                <Skeleton width={200} height={40} />
              ) : (
                !hidePaymentButton && (
                  <Button disabled={!validForm()} className="pay-button" raised>
                    پرداخت
                  </Button>
                )
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPanel;
